import Table from 'apollo-react/components/Table';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import withActiveBar from './withActiveBar';
import withCustomCells from './withCustomCells';
import withTranslation from './withTranslation';
import { fetchTranslatedColumnList } from '../actions/TranslatedColumnListsActions';
import { TRANSLATE_DATA_COLUMN_VALUES_SEPARATOR } from '../constants/SavedSearches';
import { getIsRtlLanguage } from '../selectors/AuthSelectors';
import { getUITA } from '../utils/commonUtils';
import { DATE_FORMAT } from '../utils/dateUtils';
import { insert, isEmpty, isNilOrEmpty } from '../utils/ramdaUtils';
import { dataTypeFormatters, SavedSearchNameContext } from '../utils/savedSearchUtils';

const buildCustomCell = (column) => {
  const formatter = dataTypeFormatters[column.dataType];
  if (!formatter) {
    return null;
  }
  const CustomCell = ({ row }) => <>{formatter(row[column.accessor], row[column.formatType])}</>;
  return CustomCell;
};

const getTableFilters = (savedSearchFilters, dateTypeColumnNames, translateDataColumnNames) => {
  const allFilters = savedSearchFilters?.filters ?? [];

  const commonTableFilters = allFilters.reduce((acc, f) => {
    if (
      (dateTypeColumnNames.includes(f.fieldName) && f.sqlOperator === 'LT') ||
      translateDataColumnNames.includes(f.fieldName)
    ) {
      return acc;
    }

    const filterValue = dateTypeColumnNames.includes(f.fieldName)
      ? moment(f.value).format(DATE_FORMAT)
      : f.value;

    return {
      ...acc,
      [f.fieldName]: filterValue,
    };
  }, {});

  const translateDataFilters = allFilters.filter((f) =>
    translateDataColumnNames.includes(f.fieldName)
  );

  const translateDataTableFilters = translateDataFilters.reduce((acc, f) => {
    if (!acc[f.fieldName]) {
      acc[f.fieldName] = f.value;
    } else {
      acc[f.fieldName] = `${acc[f.fieldName]}${TRANSLATE_DATA_COLUMN_VALUES_SEPARATOR}${f.value}`;
    }
    return acc;
  }, {});

  const filters = { ...commonTableFilters, ...translateDataTableFilters };

  return isEmpty(filters) ? undefined : filters;
};

export const tablePaginationProps = (translateString) => ({
  // eslint-disable-next-line react/display-name
  labelDisplayedRows: ({ from, to, count }) => (
    <span style={{ whiteSpace: 'nowrap' }} ui-ta={getUITA('page-number')}>
      {`${from}-${to} ${translateString('of')} ${count}`}
    </span>
  ),
  labelRowsPerPage: translateString('Rows'),
  pageText: translateString('Page'),
  ofText: translateString('of'),
  nextButtonText: translateString('Next page'),
  lastButtonText: translateString('Last page'),
  backButtonText: translateString('Previous page'),
  firstButtonText: translateString('First page'),
  'ui-ta': getUITA('internal-pagination'),
  backButtonProps: {
    'ui-ta': getUITA('back'),
  },
  nextButtonProps: {
    'ui-ta': getUITA('next'),
  },
  SelectProps: {
    'ui-ta': getUITA('pagination-select'),
  },
  firstPageButtonProps: {
    'ui-ta': getUITA('first-page'),
  },
  lastPageButtonProps: {
    'ui-ta': getUITA('last-page'),
  },
  pageNumberInputProps: {
    'ui-ta': getUITA('pagination-select'),
    InputProps: {
      inputProps: {
        'aria-label': 'Page Number',
      },
    },
  },
  dir: 'ltr',
});

const FormattedTable = ({
  rows,
  columns,
  extraColumns = [],
  translateString,
  showFilters: showFiltersInHeader = false,
  savedSearchFilters,
  entityName,
  searchName,
  customFilter,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const tableRowHeadRef = useRef(null);
  const [showFilters, setShowFilters] = useState(showFiltersInHeader);

  const { emptySectionMessage = 'No data to display', metadata } = otherProps;

  const customColumns = columns.map((column) => ({
    ...column,
    customCell: column.customCell || buildCustomCell(column),
  }));

  const dateTypeColumnNames = customColumns
    .filter((column) => column.dataType === 'System.DateTime')
    .map((column) => column.accessor);

  const translateDataColumnNames = customColumns
    .filter((column) => column.translateData)
    .map((column) => column.accessor);

  const tableFilters = useMemo(
    () => getTableFilters(savedSearchFilters, dateTypeColumnNames, translateDataColumnNames),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedSearchFilters]
  );

  const columnsWithExtras = extraColumns.reduce((cols, extraCol) => {
    const idx = typeof extraCol.index === 'number' ? extraCol.index : cols.length;
    let newExtraColumn = extraCol;

    if (newExtraColumn.accessor === 'SELECT_ALL_HEADER') {
      // pass rows array to column header checkbox component
      newExtraColumn = { ...newExtraColumn, header: newExtraColumn.header(rows) };
    }

    if (newExtraColumn.accessor === 'select') {
      // pass columns array to column customCell component
      newExtraColumn = {
        ...newExtraColumn,
        customCell: newExtraColumn.customCell(columns),
      };
    }

    return insert(idx, newExtraColumn, cols);
  }, customColumns);

  useEffect(() => {
    if (isRtlLanguage) {
      const applyAttributeTimer = setTimeout(() => {
        return tableRowHeadRef.current?.columnHeaderRootRef.current.setAttribute('dir', 'ltr');
      }, 1000);
      return () => clearTimeout(applyAttributeTimer);
    }
  }, [isRtlLanguage]);

  useEffect(() => {
    if (!isNilOrEmpty(tableFilters)) {
      setShowFilters(true);
    }
  }, [tableFilters]);

  useEffect(() => {
    if (translateDataColumnNames.length && entityName) {
      dispatch(fetchTranslatedColumnList(entityName, searchName, customFilter, metadata));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translateDataColumnNames.length]);

  const handleToggleFilter = () => {
    setShowFilters(!showFilters);
  };

  return (
    <SavedSearchNameContext.Provider value={searchName}>
      <Table
        rows={rows?.map((r, idx) => ({ idx, ...r }))}
        columns={columnsWithExtras}
        {...otherProps}
        emptyProps={{
          text: translateString(emptySectionMessage),
        }}
        filterTooltipText={translateString('Filter')}
        sortTooltipText={translateString('Sort')}
        filterButtonLabel={translateString('Filter')}
        tablePaginationProps={tablePaginationProps(translateString)}
        ref={tableRowHeadRef}
        showFilters={showFilters}
        onToggleFilters={handleToggleFilter}
        initialFilters={tableFilters}
      />
    </SavedSearchNameContext.Provider>
  );
};

export default withCustomCells(withActiveBar(withTranslation(FormattedTable)));
